import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, query, where, orderBy } from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Plus, Trash2, Upload } from "lucide-react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { db } from "../../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../../hooks/useFirestoreCollectionOnce";
import { useEventParticipants } from "../../../hooks/useEventParticipants";
import { useDeleteTravelItem } from "../../../hooks/useTravelMutations";
import { useSnackbar } from "../../../context/SnackbarContext";
import { useEvent } from "../../../layouts/EventLayout";
import { PageHeader } from "../../../components/PageHeader";
import { DataTable, type Column } from "../../../components/DataTable";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import type {
  Audience,
  TravelItem,
  TravelItemKind,
} from "@event-planner/shared";
import {
  formatAudienceLabel,
  type AudienceLabelUser,
} from "@event-planner/shared";
import type { OverflowAction } from "../../../components/phone/OverflowMenu";
import { ExcelTravelImportDialog } from "./ExcelTravelImportDialog";

const KIND_LABEL: Record<TravelItem["details"]["kind"], string> = {
  flight: "Flight",
  lodging: "Lodging",
  transport: "Transport",
  other: "Other",
};

const TRAVEL_TABS: Array<{ value: TravelItemKind; label: string }> = [
  { value: "flight", label: "Flights" },
  { value: "lodging", label: "Lodging" },
  { value: "transport", label: "Transport" },
  { value: "other", label: "Other" },
];

function summary(item: TravelItem): string {
  const d = item.details;
  switch (d.kind) {
    case "flight": {
      const tag =
        d.direction === "inbound"
          ? "Inbound"
          : d.direction === "outbound"
            ? "Outbound"
            : null;
      const base =
        [d.airline, d.flightNumber].filter(Boolean).join(" ") || "Flight";
      return tag ? `${tag} · ${base}` : base;
    }
    case "lodging":
      return "Lodging";
    case "transport": {
      const scheduledTag = d.pickupAt ? null : "Available throughout event";
      const limitTag = d.spendLimit
        ? `up to ${d.spendLimit.amount} ${d.spendLimit.currency}`
        : null;
      const extras = [scheduledTag, limitTag].filter(Boolean).join(" · ");
      return extras ? `${d.mode} · ${extras}` : d.mode;
    }
    case "other":
      return d.title;
  }
}

export function TravelListPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event } = useEvent();
  const { showSuccess, showError } = useSnackbar();
  const { remove, loading: deleting } = useDeleteTravelItem();
  const [deleteTarget, setDeleteTarget] = useState<TravelItem | null>(null);
  const [importMenuAnchor, setImportMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [importKind, setImportKind] = useState<TravelItemKind | null>(null);
  const [tab, setTab] = useState<TravelItemKind>("flight");

  const itemsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "travelItems"),
            where("eventId", "==", eventId),
            orderBy("createdAt", "desc"),
          )
        : null,
    [eventId],
  );

  const {
    data: items,
    loading,
    refetch,
  } = useFirestoreCollectionOnce<TravelItem>(itemsQuery);

  // Resolve audienceId → audience doc for the Travelers column.
  const audiencesQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "audiences"),
            where("eventId", "==", eventId),
          )
        : null,
    [eventId],
  );
  const { data: audiences } =
    useFirestoreCollectionOnce<Audience>(audiencesQuery);
  const { persons: eventMembers } = useEventParticipants(eventId, [
    "active",
    "pending",
  ]);
  const audienceById = useMemo(
    () => new Map(audiences.map((a) => [a.id, a])),
    [audiences],
  );
  const userById = useMemo<Map<string, AudienceLabelUser>>(
    () =>
      new Map(
        eventMembers.map((p) => [p.id, { id: p.id, displayName: p.displayName }]),
      ),
    [eventMembers],
  );

  const travelCounts = useMemo(() => {
    const counts: Record<TravelItemKind, number> = {
      flight: 0,
      lodging: 0,
      transport: 0,
      other: 0,
    };
    for (const item of items) counts[item.details.kind] += 1;
    return counts;
  }, [items]);

  const currentItems = useMemo(
    () => items.filter((item) => item.details.kind === tab),
    [items, tab],
  );

  const emptyMessage =
    items.length === 0
      ? "No travel items yet. Add flights, lodging, ground transport, or catchall entries — each targets groups and/or individual travelers, with an optional push reminder."
      : `No ${TRAVEL_TABS.find((t) => t.value === tab)?.label.toLowerCase() ?? "travel items"} yet.`;

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      showSuccess("Travel item deleted");
      setDeleteTarget(null);
      refetch();
    } catch {
      showError("Failed to delete travel item");
    }
  }

  const columns: Column<TravelItem>[] = [
    { id: "type", label: "Type", render: (i) => KIND_LABEL[i.details.kind] },
    { id: "summary", label: "Summary", render: (i) => summary(i), phone: "secondary" },
    {
      id: "audience",
      label: "Travelers",
      phone: "chips",
      render: (i) => {
        if (!i.audienceId) {
          return (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          );
        }
        const audience = audienceById.get(i.audienceId);
        return (
          <Chip
            label={formatAudienceLabel(audience, audienceById, userById)}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      id: "reminder",
      label: "Reminder",
      phone: "chips",
      align: "center",
      render: (i) =>
        i.reminder?.enabled ? (
          <Chip
            size="small"
            label={
              i.reminderSentAt ? "fired" : `${i.reminder.leadMinutes}m before`
            }
            color={i.reminderSentAt ? "default" : "primary"}
            variant="outlined"
          />
        ) : null,
    },
    {
      id: "actions",
      label: "",
      align: "right",
      phone: "hidden",
      render: (i) => (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(i);
            }}
            aria-label="Delete travel item"
          >
            <Trash2 size={18} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Phone: delete moves from the row icon to the row menu.
  function travelRowActions(i: TravelItem): OverflowAction[] {
    return [
      {
        label: "Delete travel item",
        icon: <Trash2 size={18} />,
        onClick: () => setDeleteTarget(i),
        destructive: true,
      },
    ];
  }

  return (
    <Box data-testid="travel-page">
      <PageHeader
        title="Travel"
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Upload size={16} />}
              onClick={(e) => setImportMenuAnchor(e.currentTarget)}
              data-testid="import-travel-button"
            >
              Import
            </Button>
            <Menu
              anchorEl={importMenuAnchor}
              open={!!importMenuAnchor}
              onClose={() => setImportMenuAnchor(null)}
            >
              {(
                [
                  { kind: "flight", label: "Flights" },
                  { kind: "lodging", label: "Lodging" },
                  { kind: "transport", label: "Transport" },
                  { kind: "other", label: "Other Travel" },
                ] as const
              ).map(({ kind, label }) => (
                <MenuItem
                  key={kind}
                  onClick={() => {
                    setImportKind(kind);
                    setImportMenuAnchor(null);
                  }}
                  data-testid={`import-travel-${kind}`}
                >
                  {label}
                </MenuItem>
              ))}
            </Menu>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => navigate("new")}
              data-testid="new-travel-item-button"
            >
              New Travel Item
            </Button>
          </Stack>
        }
      />
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value as TravelItemKind)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TRAVEL_TABS.map(({ value, label }) => (
          <Tab
            key={value}
            value={value}
            label={`${label} (${travelCounts[value]})`}
            data-testid={`travel-tab-${value}`}
          />
        ))}
      </Tabs>

      {!loading && currentItems.length === 0 ? (
        <Typography color="text.secondary">{emptyMessage}</Typography>
      ) : (
        <DataTable
          columns={columns}
          data={currentItems}
          loading={loading}
          ariaLabel={`${TRAVEL_TABS.find((t) => t.value === tab)?.label ?? "Travel items"} table`}
          getRowKey={(i) => i.id}
          onRowClick={(i) => navigate(i.id)}
          phoneRowActions={travelRowActions}
          phoneRowActionsLabel={(i) => `More actions for ${KIND_LABEL[i.details.kind]} ${summary(i)}`}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Travel Item"
        message="Delete this travel item? This cannot be undone."
        loading={deleting}
      />

      {eventId && event && importKind && (
        <ExcelTravelImportDialog
          open
          onClose={() => setImportKind(null)}
          eventId={eventId}
          timezone={event.timezone}
          kind={importKind}
          onImported={refetch}
        />
      )}
    </Box>
  );
}
